import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FleetRun, FleetSession, fetchRun, fetchRuns } from '../../services/fleetHudApi';
import { formatDuration, formatElapsedSince, parseTs } from './fleetTime';
import {
  itemBrief,
  missionDescription,
  missionName,
  outcomeTally,
  taskForItem,
} from './missionBrief';

/** BUILD MANIFEST status glyph — house palette: brand for landed, the one red
 * for dead work (reason always shown), muted hollow for held/other. */
function manifestGlyph(status?: string): { glyph: string; cls: string } {
  const s = String(status ?? '');
  if (s === 'merged' || s === 'completed') return { glyph: '▣', cls: 'text-brand' };
  if (s === 'rolled_back' || s.startsWith('failed')) return { glyph: '▨', cls: 'text-red-500' };
  return { glyph: '▢', cls: 'text-text-muted' };
}

type SessionsState = 'loading' | 'error' | FleetSession[];

function statusClass(status?: string): string {
  switch (status) {
    case 'in_progress':
      return 'text-brand';
    case 'failed':
    case 'error':
    case 'failed_catastrophic':
      return 'text-red-500';
    case 'human_escalated':
      return 'text-brand';
    default:
      return 'text-text-secondary';
  }
}

interface RecentRunsProps {
  /** Bridge right-rail density (item 6): 32px rows, tighter padding. */
  dense?: boolean;
  /** Inside a RailBlock the block renders the panel title — suppress ours. */
  hideHeader?: boolean;
  /** Wave-3 replay: called when a terminal row's REPLAY button is clicked.
      The parent mounts the ReplayPlayer overlay for that run. */
  onReplayRun?: (runId: string) => void;
  className?: string;
}

export default function RecentRuns({ dense = false, hideHeader = false, onReplayRun, className }: RecentRunsProps) {
  const [runs, setRuns] = useState<FleetRun[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sessions, setSessions] = useState<Record<string, SessionsState>>({});

  // Layout-only switches — no logic or data-fetch behaviour changes.
  const padY = dense ? 'py-0.5' : 'py-2';
  const rowH = dense ? ' h-8' : '';

  // The runs list itself loads once on mount; only per-run session detail is
  // interaction-gated (row expand below).
  useEffect(() => {
    let cancelled = false;
    fetchRuns(15)
      .then((r) => { if (!cancelled) setRuns(Array.isArray(r) ? r : []); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, []);

  const toggleRow = (runId: string) => {
    const next = !expanded[runId];
    setExpanded((prev) => ({ ...prev, [runId]: next }));
    // Lazy session fetch: exactly one request per run, only on expand.
    if (next && !sessions[runId]) {
      setSessions((prev) => ({ ...prev, [runId]: 'loading' }));
      fetchRun(runId)
        .then((s) => setSessions((prev) => ({ ...prev, [runId]: Array.isArray(s) ? s : [] })))
        .catch(() => setSessions((prev) => ({ ...prev, [runId]: 'error' })));
    }
  };

  const renderSessions = (runId: string) => {
    const s = sessions[runId];
    if (!s || s === 'loading') {
      return <p className="px-3 py-2 text-xs font-mono text-text-muted">Loading sessions…</p>;
    }
    if (s === 'error') {
      return <p className="px-3 py-2 text-xs font-mono text-red-500">Sessions unavailable.</p>;
    }
    if (s.length === 0) {
      return <p className="px-3 py-2 text-xs font-mono text-text-muted">No sessions recorded.</p>;
    }
    return (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-text-muted">
            <th className="px-3 py-1 font-medium">agent</th>
            <th className="px-3 py-1 font-medium">status</th>
            <th className="px-3 py-1 font-medium">model</th>
            <th className="px-3 py-1 font-medium text-right">tokens</th>
          </tr>
        </thead>
        <tbody>
          {s.map((sess, i) => (
            <tr key={sess.session_id ?? i} className="border-t border-hairline">
              <td className="px-3 py-1 font-mono text-text-primary">{sess.agent_type ?? '—'}</td>
              <td className={`px-3 py-1 ${statusClass(sess.status)}`}>{sess.status ?? '—'}</td>
              <td className="px-3 py-1 font-mono text-text-muted">{sess.model_used ?? '—'}</td>
              <td className="px-3 py-1 font-mono text-right text-text-secondary">
                {typeof sess.tokens_consumed === 'number' ? sess.tokens_consumed.toLocaleString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <section className={`rounded-lg border border-hairline bg-surface${className ? ` ${className}` : ''}`}>
      {!hideHeader && (
        <header className="border-b border-hairline px-5 py-3">
          <h2 className="text-sm font-semibold tracking-wide text-text-primary">RECENT RUNS</h2>
        </header>
      )}

      {loadError ? (
        <p className="px-5 py-4 text-sm text-red-500">Could not load runs.</p>
      ) : runs === null ? (
        <p className="px-5 py-4 text-sm text-text-muted">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="px-5 py-4 text-sm text-text-muted">No runs recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-xs text-text-muted">
                <th className={`px-5 ${padY} font-medium`} />
                <th className={`px-3 ${padY} font-medium text-right`}>started</th>
                <th className={`px-3 ${padY} font-medium text-right`}>duration</th>
                <th className={`px-3 ${padY} font-medium`}>status</th>
                <th className={`px-5 ${padY} font-medium`}>items</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const isOpen = !!expanded[run.run_id];
                const startedMs = parseTs(run.created_at);
                const duration =
                  run.status === 'in_progress'
                    ? `running ${formatElapsedSince(run.created_at)}`
                    : formatDuration(run.created_at, run.completed_at);
                const healed = run.items_healed ?? 0;
                const rolledBack = run.items_rolled_back ?? 0;
                const escCount = Array.isArray(run.escalation_reports) ? run.escalation_reports.length : 0;
                // Terminal = this component's existing terminal notion: any
                // known status that isn't 'in_progress' (the same split the
                // duration column uses). RED LINE: in_progress rows NEVER get
                // a REPLAY button.
                const terminal = !!run.status && run.status !== 'in_progress';
                return (
                  <React.Fragment key={run.run_id}>
                    <tr
                      className={`border-b border-hairline cursor-pointer hover:bg-hover${rowH}`}
                      onClick={() => toggleRow(run.run_id)}
                    >
                      <td className={`px-5 ${padY} w-8`}>
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 text-text-muted" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-text-muted" />
                        )}
                      </td>
                      <td className={`px-3 ${padY} font-mono text-right text-text-secondary whitespace-nowrap`}>
                        {/* Dense rail (bridge): the items-column REPLAY lives past the
                            right edge of the narrow rail — undiscoverable (owner hit
                            this 2026-08-14). Surface a compact ▶ up front instead.
                            Same red line: in_progress rows NEVER get it. */}
                        {dense && terminal && onReplayRun && (
                          <button
                            type="button"
                            className="mr-2 font-mono text-xs text-brand hover:underline"
                            title="Replay this run"
                            aria-label={`Replay run ${run.run_id}`}
                            data-testid={`replay-${run.run_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onReplayRun(run.run_id);
                            }}
                          >
                            ▶
                          </button>
                        )}
                        {startedMs !== null ? new Date(startedMs).toLocaleString() : '—'}
                      </td>
                      <td className={`px-3 ${padY} font-mono text-right text-text-secondary whitespace-nowrap`}>
                        {duration}
                      </td>
                      <td className={`px-3 ${padY} whitespace-nowrap ${statusClass(run.status)}`}>
                        {run.status ?? '—'}
                      </td>
                      <td className={`px-5 ${padY} text-text-secondary`}>
                        {/* WHAT WAS BUILT, zero clicks (K3 rank-1 surface): the
                            mission name + outcome tally lead; ops counts follow. */}
                        {missionName(run) && (
                          <span
                            className="mr-2 font-mono text-xs text-text-primary"
                            data-testid={`mission-${run.run_id}`}
                          >
                            {missionName(run)}
                          </span>
                        )}
                        {outcomeTally(run) && (
                          <span className="mr-2 font-mono text-xs text-brand">{outcomeTally(run)}</span>
                        )}
                        <span className="font-mono text-xs">
                          {healed} healed · {rolledBack} rolled back · {escCount} esc
                        </span>
                        {(run.items_summary ?? [])
                          .filter((it) => it.pr_url || it.commit)
                          .map((it, i) => (
                            <span key={i} className="ml-2 font-mono text-xs whitespace-nowrap">
                              {it.pr_url ? (
                                <a
                                  href={it.pr_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-brand hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  PR #{it.pr_url.split('/').pop()} ↗
                                </a>
                              ) : null}
                              {it.commit ? (
                                <span className="ml-1 text-text-muted">{it.commit.slice(0, 8)}</span>
                              ) : null}
                            </span>
                          ))}
                        {/* Wide (console) rows keep the labeled button here; dense rows
                            moved it to the ▶ in the started cell (one button per row —
                            duplicate testids would break queries and confuse SRs). */}
                        {!dense && terminal && (
                          <button
                            type="button"
                            className="ml-2 font-mono text-xs text-brand hover:underline"
                            data-testid={`replay-${run.run_id}`}
                            onClick={(e) => {
                              // Never let the replay gesture expand the row.
                              e.stopPropagation();
                              onReplayRun?.(run.run_id);
                            }}
                          >
                            REPLAY
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-hairline">
                        <td colSpan={5} className="bg-elevated p-0">
                          <div className="px-3 pt-2 font-mono text-xs text-text-muted">
                            {run.run_id}
                          </div>
                          {/* MISSION — operator-authored, verbatim (▍ gutter =
                              quoted recorded text, quote-only doctrine). */}
                          {missionDescription(run) && (
                            <div className="px-3 pt-2" data-testid={`mission-desc-${run.run_id}`}>
                              <p className="font-mono text-[10px] uppercase tracking-wide text-text-muted">
                                mission
                              </p>
                              <p className="border-l-2 border-brand pl-2 font-mono text-xs leading-5 text-text-secondary">
                                {missionDescription(run)}
                              </p>
                            </div>
                          )}
                          {/* BUILD MANIFEST — per item: status glyph + reason,
                              a QUOTED brief trimmed from the real task spec
                              (deterministic itemBrief, never a paraphrase),
                              then the recorded meta (PR / branch / commit). */}
                          {(run.items_summary ?? []).length > 0 && (
                            <div className="px-3 pt-2" data-testid={`manifest-${run.run_id}`}>
                              <p className="font-mono text-[10px] uppercase tracking-wide text-text-muted">
                                build manifest
                              </p>
                              {(run.items_summary ?? []).map((it, i) => {
                                const g = manifestGlyph(it.status);
                                const brief = itemBrief(taskForItem(run, it.item));
                                return (
                                  <div key={it.item ?? i} className="py-0.5 font-mono text-xs leading-5">
                                    <p className="truncate">
                                      <span className={g.cls}>{g.glyph}</span>{' '}
                                      <span className="text-text-primary">{it.item ?? `item_${i + 1}`}</span>{' '}
                                      <span className="uppercase text-text-secondary">{it.status ?? '—'}</span>
                                      {it.reason && <span className="text-text-muted"> · {it.reason}</span>}
                                    </p>
                                    {brief && (
                                      <p className="truncate pl-4 text-text-secondary">“{brief}”</p>
                                    )}
                                    {(it.pr_url || it.branch || it.commit) && (
                                      <p className="truncate pl-4 text-text-muted">
                                        {it.pr_url && (
                                          <a
                                            href={it.pr_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-brand hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            PR #{it.pr_url.split('/').pop()} ↗
                                          </a>
                                        )}
                                        {it.branch && <span className="ml-1.5">{it.branch}</span>}
                                        {it.commit && <span className="ml-1.5">{it.commit.slice(0, 8)}</span>}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* TELEMETRY — sessions demoted below the manifest:
                              it answers "how did it run", the secondary question. */}
                          <p className="px-3 pt-2 font-mono text-[10px] uppercase tracking-wide text-text-muted">
                            telemetry
                          </p>
                          {renderSessions(run.run_id)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
